import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { List, X, Globe } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage, t } = useLanguage();

  const navLinks = [
    { href: '/', label: t('nav.home') },
    { href: '/produk', label: t('nav.products') },
    { href: '/industri', label: t('nav.industry') },
    { href: '/case-study', label: t('nav.caseStudy') },
    { href: '/tentang', label: t('nav.about') },
  ];

  return (
    <header 
      className="fixed top-0 left-0 right-0 z-50 glass"
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3"
            data-testid="logo-link"
          >
            <img 
              src="https://xgrids.com/assets/logo_white.ceffc551.png" 
              alt="XGRIDS" 
              className="h-7 lg:h-8"
            />
            <span className="font-heading font-bold text-lg text-xgrids-brand-primary">.ID</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors duration-200 link-animated ${
                  location.pathname === link.href 
                    ? 'text-xgrids-brand-primary' 
                    : 'text-xgrids-text-secondary hover:text-xgrids-text-primary'
                }`}
                data-testid={`nav-link-${link.href.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right side: Language + CTA */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-2 text-sm text-xgrids-text-secondary hover:text-white transition-colors border border-white/10 hover:border-white/20"
              data-testid="language-switcher"
            >
              <Globe size={18} />
              <span className="font-mono">{language.toUpperCase()}</span>
            </button>

            <Link
              to="/tentang#contact"
              className="btn-primary text-sm rounded-none"
              data-testid="contact-cta-btn"
            >
              {t('nav.contactUs')}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-3">
            {/* Mobile Language Switcher */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1 px-2 py-1 text-sm text-xgrids-text-secondary border border-white/10"
              data-testid="mobile-language-switcher"
            >
              <Globe size={16} />
              <span className="font-mono text-xs">{language.toUpperCase()}</span>
            </button>
            
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-xgrids-text-primary"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <List size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass border-t border-white/10"
            data-testid="mobile-menu"
          >
            <nav className="px-6 py-4 flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`text-base font-medium py-2 ${
                    location.pathname === link.href 
                      ? 'text-xgrids-brand-primary' 
                      : 'text-xgrids-text-secondary'
                  }`}
                  data-testid={`mobile-nav-${link.href.replace('/', '') || 'home'}`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/tentang#contact"
                onClick={() => setMobileMenuOpen(false)}
                className="btn-primary text-center text-sm rounded-none mt-2"
                data-testid="mobile-contact-btn"
              >
                {t('nav.contactUs')}
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
